import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  applyNoc,
  getNocEligibility,
  getNocTypes,
  payNocSuccess,
} from "../../../store/actions/centerActions/nocAction";
import { NOC_DIGITAL_NOTE, openNocCheckout } from "../../../utils/nocPay";
import { errorAlert, successAlert } from "../../../utils/alert";

/*
  A center applies for a NOC for one of its students, straight from the student
  list. Pre-filled with the student's name; the center only picks the NOC type
  and the validity, then pays. Same flow as the student's own screen.

  Props: student = { id, name }, show, onHide, onDone
*/
const NocApplyModal = ({ student, show, onHide, onDone }) => {
  const dispatch = useDispatch();

  const [types, setTypes] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [typeId, setTypeId] = useState("");
  const [validity, setValidity] = useState("3");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (show) {
      setTypeId("");
      setValidity("3");
      setEligibility(null);
      dispatch(getNocTypes()).then((rows) =>
        setTypes(Array.isArray(rows) ? rows : [])
      );
      if (student?.id) {
        dispatch(getNocEligibility(student.id)).then((e) => setEligibility(e));
      }
    }
  }, [show, dispatch, student]);

  const blocked = eligibility && eligibility.ok === false;

  const selected = types.find((t) => String(t.id) === String(typeId));
  const fee = selected
    ? Number(validity) === 6
      ? Number(selected.fee_6m)
      : Number(selected.fee_3m)
    : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!typeId) return errorAlert("Please choose a NOC type.");

    setSending(true);
    const body = new FormData();
    body.append("student_id", student.id);
    body.append("noc_type_id", typeId);
    body.append("validity_months", validity);
    const res = await dispatch(applyNoc(body));

    if (res.status !== "success") {
      errorAlert(res.message || "The NOC could not be applied for.");
      setSending(false);
      return;
    }

    const d = res.data || {};

    if (d.free) {
      successAlert("NOC issued.");
      setSending(false);
      onHide();
      onDone && onDone();
      return;
    }

    const pay = await openNocCheckout({ ...d, name: student.name });
    if (pay.payment_id) {
      const fd = new FormData();
      fd.append("noc_id", d.noc_id);
      fd.append("razorpay_payment_id", pay.payment_id);
      const done = await dispatch(payNocSuccess(fd));
      if (done.status === "success") {
        successAlert("Payment verified. NOC issued.");
        onHide();
        onDone && onDone();
      } else {
        errorAlert(done.message || "Payment could not be verified.");
      }
    } else if (pay.error) {
      errorAlert(pay.error);
    }
    setSending(false);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Apply for NOC</Modal.Title>
      </Modal.Header>
      <Form onSubmit={submit}>
        <Modal.Body>
          <p className="mb-3">
            Student : <b>{student?.name}</b>
          </p>

          {blocked ? (
            <div className="alert alert-warning mb-0">{eligibility.reason}</div>
          ) : types.length === 0 ? (
            <p>No NOC type is available right now.</p>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Purpose (NOC Type)</Form.Label>
                <Form.Control
                  as="select"
                  value={typeId}
                  onChange={(e) => setTypeId(e.target.value)}
                >
                  <option value="">-- Select NOC type --</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.subject} (Form {t.form_no})
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Validity</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    id="cnoc-v3"
                    label={"03 Months" + (selected ? ` (₹${Number(selected.fee_3m)})` : "")}
                    checked={validity === "3"}
                    onChange={() => setValidity("3")}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="cnoc-v6"
                    label={"06 Months" + (selected ? ` (₹${Number(selected.fee_6m)})` : "")}
                    checked={validity === "6"}
                    onChange={() => setValidity("6")}
                  />
                </div>
              </Form.Group>

              {selected && (
                <p className="mb-2">
                  Amount payable : <b>₹{fee}</b>
                </p>
              )}

              <p className="text-muted" style={{ fontSize: "13px" }}>
                {NOC_DIGITAL_NOTE}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onHide}>
            Cancel
          </Button>
          <Button type="submit" className="btn btn-comman" disabled={sending || !typeId || blocked}>
            {sending ? "Please wait..." : fee > 0 ? `Apply & Pay ₹${fee}` : "Apply for NOC"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default NocApplyModal;
